import { z } from 'zod'

import { subscriptionStatusSchema, lockedResourceSchema } from './subscription'

export const developerWorkspaceSchema = z.object({
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  subscriptionId: z.string().uuid().nullable().optional(),
  planCode: z.string().nullable().optional(),
  planPrice: z.number().optional(),
  subscriptionStatus: z.string().nullable().optional(),
  periodStart: z.string().nullable().optional(),
  periodEnd: z.string().nullable().optional(),
  cancelAtPeriodEnd: z.boolean().optional(),
  lockedResourceCount: z.number().int().nonnegative().default(0),
})

export const developerWorkspaceDetailSchema = developerWorkspaceSchema.extend({
  ownerUserId: z.string().uuid().nullable().optional(),
  ownerName: z.string().nullable().optional(),
  ownerEmail: z.string().nullable().optional(),
  createdAt: z.string(),
  subscription: subscriptionStatusSchema.nullable().optional(),
  usage: z.record(z.unknown()),
  memberCount: z.number().int().nonnegative(),
  recordGroupCount: z.number().int().nonnegative(),
  recordCount: z.number().int().nonnegative(),
  automationCount: z.number().int().nonnegative(),
  taskCount: z.number().int().nonnegative(),
  lockedResources: z.array(lockedResourceSchema),
  recentActivities: z.array(z.record(z.unknown())),
})

export const developerWorkspaceListRowSchema = z.object({
  workspaceId: z.string().uuid(),
  workspaceName: z.string(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  planCode: z.string().nullable(),
  planName: z.string().nullable(),
  subscriptionStatus: z.string().nullable(),
  createdAt: z.string(),
  lockedResourceCount: z.number().int().nonnegative(),
})

export const developerWorkspacePageSchema = z.object({
  content: z.array(developerWorkspaceListRowSchema),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
})

export const developerUserSchema = z.object({
  userId: z.string().uuid(),
  fullName: z.string().nullable(),
  email: z.string().email(),
  workspaces: z.array(z.object({
    workspaceId: z.string().uuid(),
    workspaceName: z.string(),
      superUser: z.boolean(),
    roleNames: z.array(z.string()),
  })),
})

export const developerUsersPageSchema = z.object({
  content: z.array(developerUserSchema),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
})

export const developerSystemAnalyticsSchema = z.object({
  summary: z.record(z.union([z.string(), z.number(), z.boolean()])),
  workspaceStatusCounts: z.array(z.record(z.unknown())),
  planCounts: z.array(z.object({ label: z.string(), value: z.number() })),
  recordGrowth: z.array(z.object({ label: z.string(), value: z.number() })),
})

export const forceSetSubscriptionRequestSchema = z.object({
  templateCode: z.string(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
})

export type DeveloperWorkspace = z.infer<typeof developerWorkspaceSchema>
export type DeveloperWorkspaceDetail = z.infer<typeof developerWorkspaceDetailSchema>
export type DeveloperWorkspaceListRow = z.infer<typeof developerWorkspaceListRowSchema>
export type DeveloperWorkspacePage = z.infer<typeof developerWorkspacePageSchema>
export type DeveloperUser = z.infer<typeof developerUserSchema>
export type DeveloperUsersPage = z.infer<typeof developerUsersPageSchema>
export type DeveloperSystemAnalytics = z.infer<typeof developerSystemAnalyticsSchema>
export type ForceSetSubscriptionRequest = z.infer<typeof forceSetSubscriptionRequestSchema>